/**
 * Renders `mfaMandatoryFrom` for the enrollment banner.
 *
 * The value is a plain Belgium-local calendar date — "2026-09-15", never "2026-09-15T00:00:00Z". It
 * is deliberately NOT handed to `new Date(value)`: that parses a bare ISO date as midnight *UTC*, so
 * every user west of Greenwich would be told MFA becomes mandatory a day early. Splitting the parts
 * by hand builds a local Date on the right calendar day in every timezone.
 */

/**
 * i18next gives us 'nl' | 'fr' | 'en'. Belgian variants are used rather than the bare tags because
 * the agency is Belgian: bare 'en' formats US-style ("September 15, 2026"), where 'en-GB' gives the
 * "15 September 2026" the story's copy is written around.
 */
const LOCALE_BY_LANGUAGE: Record<string, string> = {
  nl: 'nl-BE',
  fr: 'fr-BE',
  en: 'en-GB',
};

const FALLBACK_LOCALE = 'en-GB';

/** '' when there is no usable date — callers must not interpolate that into "mandatory on {date}". */
export const formatMfaMandatoryDate = (value: string | null, language: string): string => {
  const [year, month, day] = `${value ?? ''}`.split('-').map(Number);
  if (!year || !month || !day) {
    return '';
  }

  // i18next hands back things like 'en-US' or 'nl-BE' once a region has been negotiated, so match on
  // the language subtag rather than the whole tag.
  const locale = LOCALE_BY_LANGUAGE[`${language ?? ''}`.slice(0, 2).toLowerCase()] ?? FALLBACK_LOCALE;

  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(
    new Date(year, month - 1, day),
  );
};

export default formatMfaMandatoryDate;
